import React, { useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { LicensePlan } from "../../../store/actions/PlanActions";
import { showModal } from "../../../store/actions/modalActions";

/* ─────────────────────────────────────────────────────────
   Section & AI Feature → human-readable labels
   ───────────────────────────────────────────────────────── */
const getSectionLabels = (t: (key: string) => string): Record<string, string> => ({
  matters: t("Case Management"),
  clients: t("Client Management"),
  tasks: t("Tasks & Deadlines"),
  files: t("Document Management"),
  research: t("AI Legal Research"),
  tables: t("AI Due Diligence Tables"),
  analytics: t("Analytics & Reporting"),
  data_sources: t("Knowledge Base (RAG)"),
  templates: t("Document Templates"),
  client_pipeline: t("Client Pipeline & AI Intake"),
  finance: t("Finance & Billing"),
  people: t("People Management"),
  departments: t("Departments"),
  teams: t("Teams"),
  calendar: t("Calendar"),
  messages: t("Message Center"),
});

const getAiFeatureLabels = (t: (key: string) => string): Record<string, string> => ({
  drafts: t("AI Document Drafts"),
  insights: t("AI Matter Insights"),
  research: t("AI Legal Research"),
  tables_ai: t("AI Table Analysis"),
  onboarding_ai: t("AI Client Onboarding"),
  template_ai: t("AI Template Generation"),
  data_source_indexing: t("Knowledge Base Indexing"),
});

/* ─────────────────────────────────────────────────────────
   Find which plan/addon unlocks a given section or feature
   ───────────────────────────────────────────────────────── */
function findUnlockingPlan(
  section: string | null,
  aiFeature: string | null,
  plans: LicensePlan[],
  addons: LicensePlan[]
): { plan: LicensePlan | null; isAddon: boolean } {
  for (const addon of addons) {
    if (
      (section && addon.feature_gates?.sections?.includes(section)) ||
      (aiFeature && addon.feature_gates?.ai_features?.includes(aiFeature))
    ) {
      return { plan: addon, isAddon: true };
    }
  }
  for (const plan of plans) {
    if (
      (section && plan.feature_gates?.sections?.includes(section)) ||
      (aiFeature && plan.feature_gates?.ai_features?.includes(aiFeature))
    ) {
      return { plan, isAddon: false };
    }
  }
  return { plan: null, isAddon: false };
}

/* ─────────────────────────────────────────────────────────
   LicenseErrorHandler — global listener for 403 licensing errors
   from the axios interceptor. Shows the upgrade modal.
   
   Mount once in App.tsx or your top-level layout.
   ───────────────────────────────────────────────────────── */
export default function LicenseErrorHandler() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const plans = useSelector((state: RootState) => state.license?.plans ?? []);
  const addons = useSelector((state: RootState) => state.license?.addons ?? []);

  const handleLicenseError = useCallback(
    (event: Event) => {
      // Skip if the LicenseOverlay is already showing on this page
      if ((window as any).__ponsLicenseOverlayActive) return;

      const detail = (event as CustomEvent).detail;
      if (!detail?.code) return;

      const { code, required_section, required_feature, limit_key, current_count, limit_value } = detail;

      const sectionLabels = getSectionLabels(t);
      const aiFeatureLabels = getAiFeatureLabels(t);

      let title = t("Upgrade Required");
      let description = detail.message || "";

      switch (code) {
        case "LICENSE_EXPIRED": {
          title = t("License Expired");
          description =
            "Your organization's license has expired. Please renew to continue using PONS.";
          break;
        }
        case "SECTION_NOT_LICENSED": {
          const sectionLabel = sectionLabels[required_section] || required_section || t("this feature");
          const { plan, isAddon } = findUnlockingPlan(required_section, null, plans, addons);
          description = `Your current plan does not include access to <strong>${sectionLabel}</strong>. ${
            isAddon && plan
              ? `Add the <strong>${plan.display_name}</strong> add-on to unlock this feature.`
              : plan
              ? `Upgrade to <strong>${plan.display_name}</strong> to unlock this feature.`
              : t("Please upgrade your plan to access this feature.")
          }`;
          title = sectionLabel;
          break;
        }
        case "AI_FEATURE_NOT_LICENSED": {
          const featureLabel = aiFeatureLabels[required_feature] || required_feature || t("this AI feature");
          const { plan, isAddon } = findUnlockingPlan(null, required_feature, plans, addons);
          description = `Your current plan does not include <strong>${featureLabel}</strong>. ${
            isAddon && plan
              ? `Add the <strong>${plan.display_name}</strong> add-on to unlock this capability.`
              : plan
              ? `Upgrade to <strong>${plan.display_name}</strong> to unlock this capability.`
              : t("Please upgrade your plan to access this capability.")
          }`;
          title = featureLabel;
          break;
        }
        case "LICENSE_READ_ONLY": {
          title = t("Read-Only Mode");
          description =
            "Your organization's license has expired. You can view existing data but cannot make changes. Renew your license to restore full access.";
          break;
        }
        case "USAGE_LIMIT_REACHED": {
          const limitLabel = (limit_key || "").replace("max_", "").replace(/_/g, " ");
          title = t("Usage Limit Reached");
          description = `You've used <strong>${current_count || "all"}</strong> of your <strong>${limit_value || ""}</strong> ${limitLabel}. Upgrade your plan to increase this limit.`;
          break;
        }
        case "NO_BASE_SEAT": {
          title = t("No Active Seat");
          description =
            "You don't currently have a seat on your organization's plan. Ask your admin to assign you a seat to access this feature.";
          break;
        }
      }

      dispatch(
        showModal({
          type: "license-request",
          showModal: true,
          message: title,
          subMsg: description,
          title,
          onConfirm: () => {
            history.push("/account?tab=plans");
          },
        })
      );
    },
    [dispatch, history, plans, addons, t]
  );

  useEffect(() => {
    window.addEventListener("license-error", handleLicenseError);
    return () => {
      window.removeEventListener("license-error", handleLicenseError);
    };
  }, [handleLicenseError]);

  return null; // This component renders nothing — it's just an event listener
}
